import React, { useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";

import QuizQuestionModal from "./QuizQuestionModal";
import {
  QUIZ_MAX_QUESTIONS,
  QUIZ_PAGE_SIZES,
  downloadSampleQuizCsv,
  exportQuizCsv,
  parseQuizCsv,
  validateCrossroads,
} from "./quizHelpers";

function truncate(text, max = 80) {
  const s = String(text ?? "");
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

export default function QuizTab({
  questions,
  source,
  isLoading,
  isSaving,
  themeColors,
  hasCorrectAnswers = true,
  onSaveQuestion,
  onDeleteQuestion,
  onDeleteAll,
  onImportCsv,
  onReorder,
  errorMessage,
  successMessage,
  setErrorMessage,
  setSuccessMessage,
}) {
  const fileInputRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  const openAdd = () => {
    if (questions.length >= QUIZ_MAX_QUESTIONS) {
      setErrorMessage(`You can add up to ${QUIZ_MAX_QUESTIONS} crossroads.`);
      return;
    }
    setErrorMessage("");
    setModalMode("add");
    setEditingQuestion(null);
    setModalOpen(true);
  };

  const openEdit = (q) => {
    setErrorMessage("");
    setModalMode("edit");
    setEditingQuestion(q);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingQuestion(null);
  };

  const handleModalSubmit = async ({ question }) => {
    setErrorMessage("");
    try {
      await onSaveQuestion({
        action: modalMode,
        editId: modalMode === "edit" ? editingQuestion?.id : null,
        question,
      });
      closeModal();
    } catch {
      /* parent surfaces error */
    }
  };

  const handleDelete = async (q) => {
    const result = await Swal.fire({
      title: "Delete this crossroads?",
      text: truncate(q.question_name, 80),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#ef4444",
    });
    if (!result.isConfirmed) return;
    await onDeleteQuestion(q.id);
  };

  const handleDeleteAll = async () => {
    if (!questions.length) return;
    const result = await Swal.fire({
      title: "Delete all crossroads?",
      text: "This removes every crossroads for this theme. You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete All",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#ef4444",
    });
    if (!result.isConfirmed) return;
    await onDeleteAll();
  };

  const showCsvUploadError = (message) => {
    setSuccessMessage("");
    setErrorMessage(message);
    Swal.fire({ icon: "error", title: "CSV upload failed", text: message, confirmButtonText: "OK" });
  };

  const handleCsvUpload = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!/\.csv$/i.test(file.name)) {
      showCsvUploadError("Please upload a .csv file.");
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      let parsed;
      try {
        parsed = parseQuizCsv(reader.result);
      } catch (err) {
        showCsvUploadError(err?.message || "Failed to read the CSV.");
        return;
      }
      if (!parsed.length) {
        showCsvUploadError("The CSV has a header row but no crossroads.");
        return;
      }
      if (parsed.length > QUIZ_MAX_QUESTIONS) {
        showCsvUploadError(`The CSV has ${parsed.length} crossroads. The maximum is ${QUIZ_MAX_QUESTIONS}.`);
        return;
      }
      const rows = [];
      for (let i = 0; i < parsed.length; i += 1) {
        const result = validateCrossroads(parsed[i], { rowLabel: `Row ${i + 2}`, hasCorrectAnswers });
        if (!result.ok) {
          showCsvUploadError(result.message);
          return;
        }
        rows.push(result.data);
      }

      if (questions.length) {
        const confirm = await Swal.fire({
          title: `Replace ${questions.length} crossroads?`,
          text: `Uploading replaces all existing crossroads for this theme with the ${rows.length} in this file.`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Replace",
        });
        if (!confirm.isConfirmed) return;
      }

      setErrorMessage("");
      try {
        await onImportCsv(rows);
        setPage(1);
      } catch {
        /* parent surfaces error */
      }
    };
    reader.onerror = () => showCsvUploadError("Could not read the CSV file.");
    reader.readAsText(file);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return questions;
    return questions.filter((row) =>
      [row.question_name, row.option_one, row.option_two, row.correct_answer]
        .some((v) => String(v || "").toLowerCase().includes(q))
    );
  }, [questions, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageRows = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);
  const canReorder = !search.trim() && !isSaving;

  const move = (q, delta) => {
    const ids = questions.map((x) => x.id);
    const from = ids.indexOf(q.id);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= ids.length) return;
    [ids[from], ids[to]] = [ids[to], ids[from]];
    onReorder(ids);
  };

  return (
    <div className="themeupdate-panel-wide quiz-panel" data-tour="tour-gc-quiz">
      <div className="quiz-page-header">
        <h2 className="quiz-page-title">Crossroads ({questions.length})</h2>
        <div className="quiz-toolbar">
          <button type="button" className="quiz-toolbar-btn" onClick={openAdd} disabled={isSaving}>
            <i className="fa-solid fa-plus" aria-hidden /> Add Crossroads
          </button>
          <label className="quiz-toolbar-btn quiz-upload-label">
            <i className="fa-solid fa-upload" aria-hidden /> Upload CSV
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="quiz-upload-input"
              onChange={handleCsvUpload}
              disabled={isSaving}
            />
          </label>
          <button type="button" className="quiz-toolbar-btn" onClick={() => exportQuizCsv(questions)} disabled={!questions.length}>
            <i className="fa-regular fa-file-lines" aria-hidden /> Export CSV
          </button>
          <button type="button" className="quiz-toolbar-btn" onClick={downloadSampleQuizCsv}>
            <i className="fa-solid fa-download" aria-hidden /> Sample CSV
          </button>
          <button type="button" className="quiz-toolbar-btn" onClick={handleDeleteAll} disabled={isSaving || !questions.length}>
            <i className="fa-solid fa-trash" aria-hidden /> Delete All
          </button>
        </div>
      </div>

      <p className="qa-char-limit-hint">
        Each crossroads is one decision point: a scenario, two roads (with a correct answer, or choice based with no right
        answer) and a “Did You Know?” learning moment (max {QUIZ_MAX_QUESTIONS}). Players meet them in the order below. Uploading a CSV replaces all existing crossroads.
      </p>

      {source === "catalog" && questions.length ? (
        <p className="cr-alert cq-catalog-note">
          You're viewing the default crossroads for this theme. Your first change creates your own copy for this session — the
          defaults stay unchanged for everyone else.
        </p>
      ) : null}

      {(errorMessage || successMessage) && (
        <div className="qa-alerts" role="status">
          {errorMessage ? <p className="cr-alert cr-alert-error">{errorMessage}</p> : null}
          {successMessage ? <p className="cr-alert cr-alert-success">{successMessage}</p> : null}
        </div>
      )}

      <div className="quiz-table-card themeupdate-card">
        <div className="quiz-table-controls">
          <label className="quiz-table-show">
            Show{" "}
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
            >
              {QUIZ_PAGE_SIZES.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>{" "}
            entries
          </label>
          <label className="quiz-table-search">
            Search:{" "}
            <input
              type="search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </label>
        </div>

        <div className="quiz-table-wrap">
          <table className="quiz-table cq-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Scenario</th>
                <th>Roads</th>
                <th>Correct</th>
                <th>Points</th>
                <th>
                  <span className="cr-sr-only">Order</span>
                </th>
                <th>Edit</th>
                <th>Delete</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="quiz-table-empty">
                    Loading crossroads…
                  </td>
                </tr>
              ) : pageRows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="quiz-table-empty">
                    {search ? (
                      "No crossroads match your search."
                    ) : (
                      <>
                        No crossroads yet. Click <strong>Add Crossroads</strong>, or download the <strong>Sample CSV</strong> and
                        upload it to get started.
                      </>
                    )}
                  </td>
                </tr>
              ) : (
                pageRows.map((q) => {
                  const position = questions.findIndex((x) => x.id === q.id);
                  const isChoice = !hasCorrectAnswers || q.question_type === "choice";
                  const correctLetter = String(q.answer_id) === "2" ? "B" : "A";
                  return (
                    <tr key={q.id}>
                      <td>{position + 1}</td>
                      <td className="quiz-cell-question" title={q.question_name}>
                        <span className="cq-cell-title">{truncate(q.question_name, 110)}</span>
                      </td>
                      <td className="cq-cell-roads">
                        <span>
                          <b>A</b> {truncate(q.option_one, 34)}
                        </span>
                        <span>
                          <b>B</b> {truncate(q.option_two, 34)}
                        </span>
                      </td>
                      <td>
                        {isChoice ? (
                          <span className="cq-choice-pill" title="Choice based — no correct answer">Choice</span>
                        ) : (
                          <span className="cq-correct-pill">{correctLetter}</span>
                        )}
                      </td>
                      <td>{isChoice ? "—" : q.points == null || q.points === "" ? 10 : q.points}</td>
                      <td className="cq-order">
                        <button
                          type="button"
                          className="cq-order-btn"
                          onClick={() => move(q, -1)}
                          disabled={!canReorder || position === 0}
                          aria-label={`Move crossroads ${position + 1} up`}
                        >
                          <i className="fa-solid fa-arrow-up" aria-hidden />
                        </button>
                        <button
                          type="button"
                          className="cq-order-btn"
                          onClick={() => move(q, 1)}
                          disabled={!canReorder || position === questions.length - 1}
                          aria-label={`Move crossroads ${position + 1} down`}
                        >
                          <i className="fa-solid fa-arrow-down" aria-hidden />
                        </button>
                      </td>
                      <td>
                        <button type="button" className="quiz-table-btn quiz-table-btn-edit" onClick={() => openEdit(q)}>
                          <i className="fa-solid fa-pencil" aria-hidden /> Edit
                        </button>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="quiz-table-btn quiz-table-btn-delete"
                          onClick={() => handleDelete(q)}
                          disabled={isSaving}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="quiz-table-footer">
          <span className="quiz-table-info">
            Showing {filtered.length ? (safePage - 1) * pageSize + 1 : 0} to {Math.min(safePage * pageSize, filtered.length)} of{" "}
            {filtered.length} entries
            {search ? ` (filtered from ${questions.length})` : ""}
          </span>
          <div className="quiz-table-pager">
            <button type="button" className="quiz-pager-btn" disabled={safePage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              Previous
            </button>
            <span className="quiz-pager-num">
              {safePage} / {totalPages}
            </span>
            <button
              type="button"
              className="quiz-pager-btn"
              disabled={safePage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <QuizQuestionModal
        open={modalOpen}
        mode={modalMode}
        initial={editingQuestion}
        onClose={closeModal}
        onSubmit={handleModalSubmit}
        isSaving={isSaving}
        themeColors={themeColors}
        hasCorrectAnswers={hasCorrectAnswers}
      />
    </div>
  );
}
